import { NodeViewContent, NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import cn from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import TableMenu from '../TableMenu'
import styles from './TableNode.module.scss'

import { useTimeout } from '~hooks/useTimeout'
import MoreIcon from '~icons/more.svg'
import { preventDefault } from '~lib/helpers'
import IconButton from '~lib/ui-kit/IconButton'

const TableNode: React.FC<React.PropsWithChildren<NodeViewProps>> = ({ editor, node, getPos }) => {
  const [focused, setFocused] = useState(false)
  const [menuOpened, setMenuOpened] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const [menuButtonEl, setMenuButtonEl] = useState<HTMLElement>(null)
  const getStartPos = useCallback(() => getPos() + 2, [getPos])
  const cachedPos = useRef(getStartPos())
  const { setTimeout } = useTimeout()

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
      setFocused(false)
    }
  }, [])

  useEffect(() => {
    if (editor.isEditable) {
      document.addEventListener('mouseup', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside)
    }
  }, [editor.isEditable, handleClickOutside])

  function handleChangeMenuOpened(opened: boolean) {
    setMenuOpened(opened)

    if (!opened) {
      setTimeout(() => {
        editor.commands.focus(cachedPos.current)
      }, 200)
    }
  }

  function updatePos() {
    cachedPos.current = editor.state.selection.from
  }

  function handleClickWrapper(e: React.MouseEvent) {
    if (!focused && editor.isEditable) {
      if (!tableRef.current.contains(e.target as Node)) {
        cachedPos.current = getStartPos()
        editor.commands.focus(cachedPos.current)
      }

      setFocused(true)
    }

    updatePos()
  }

  function handleAddColumnAfter() {
    editor.chain().focus(cachedPos.current).addColumnAfter().run()
    updatePos()
  }

  function handleAddRowAfter() {
    editor.chain().focus(cachedPos.current).addRowAfter().run()
    updatePos()
  }

  return (
    <NodeViewWrapper data-node="table">
      <div
        ref={wrapperRef}
        className={cn(styles.root, editor.isEditable && styles.editable, focused && styles.focused, 'tableWrapper')}
        onMouseDownCapture={handleClickWrapper}
      >
        <div className={styles.container}>
          <table ref={tableRef} {...node.attrs}>
            <colgroup />
            <NodeViewContent as="tbody" className={styles.content} />
          </table>
        </div>

        <IconButton
          ref={setMenuButtonEl}
          tabIndex={-1}
          variant="secondary"
          className={styles.menuBtn}
          onMouseDown={preventDefault}
        >
          <MoreIcon />
        </IconButton>

        <button className={cn(styles.rightBtn)} type="button" onMouseDown={handleAddColumnAfter}>
          <span>+ Add column</span>
        </button>

        <button className={cn(styles.bottomBtn)} type="button" onMouseDown={handleAddRowAfter}>
          + Add row
        </button>

        <TableMenu
          editor={editor}
          onUpdate={updatePos}
          position={cachedPos.current}
          open={menuOpened}
          onChangeOpen={handleChangeMenuOpened}
          anchorEl={menuButtonEl}
          withScroll={false}
        />
      </div>
    </NodeViewWrapper>
  )
}

export default TableNode
